import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import LoadingButton from "@mui/lab/LoadingButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GavelIcon from "@mui/icons-material/Gavel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";

/**
 * 시나리오 비교 화면 공통 릴리즈 바.
 *
 * 카드마다 하나씩 있던 릴리즈 버튼을 화면당 하나로 합친 것. 카드는 "고르기"만 하고
 * 실제 릴리즈는 여기서만 일어난다. 카드에 남는 것은 상태 표시(릴리즈됨 / 릴리즈 중 / 미확정)뿐이다.
 */
export const ComparisonReleaseBar = ({ selectedName, disabled, disabledReason, isReleasing, onRelease, label, startIcon, sx }) => {
  const { t } = useTranslation();

  const releaseButton = (
    <Button
      variant="contained"
      size="large"
      startIcon={startIcon ?? <GavelIcon />}
      disabled={disabled}
      onClick={onRelease}
      sx={{ minWidth: "180px" }}>
      {label ?? t("releaseScenario")}
    </Button>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, width: "100%", py: 0.5, ...sx }}>
      {selectedName ? (
        // 고른 버전은 체크 칩 하나로만 보여준다. 옆이 바로 릴리즈 버튼이라 설명 문구는 군더더기다.
        <Chip
          size="small"
          icon={<CheckCircleIcon />}
          label={selectedName}
          title={selectedName}
          sx={theme => ({
            maxWidth: "280px",
            height: "26px",
            fontWeight: 700,
            letterSpacing: "0.01em",
            color: theme.palette.primary.dark,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            "& .MuiChip-icon": { color: theme.palette.primary.main, ml: "6px", mr: "-2px" },
          })}
        />
      ) : (
        <Typography variant="subtitle1" noWrap color="text.secondary">
          {t("clickCardToSelectLabel")}
        </Typography>
      )}
      {isReleasing ? (
        <LoadingButton
          loading
          loadingPosition="start"
          startIcon={<PlayArrowIcon />}
          variant="outlined"
          size="large"
          sx={{ minWidth: "180px" }}>
          {t("releasing")}
        </LoadingButton>
      ) : disabled && disabledReason ? (
        // 비활성 버튼은 이벤트를 삼켜서 툴팁이 안 뜬다. span 으로 한 번 감싸 줘야 한다.
        <Tooltip title={disabledReason} placement="bottom-end">
          <Box component="span" sx={{ display: "inline-flex" }}>{releaseButton}</Box>
        </Tooltip>
      ) : (
        releaseButton
      )}
    </Box>
  );
};

/**
 * 비교 카드 한 장을 고를 수 있게 감싸는 껍데기.
 *
 * 표시는 **테두리 선 하나**다. 카드 안의 차트·요약 카드·그리드는 물론
 * 카드가 놓인 바닥도 색을 칠하지 않는다.
 */
export const SelectableComparison = ({ selected, disabled, onSelect, children, sx }) => (
  <Box
    onClick={disabled ? undefined : onSelect}
    sx={theme => ({
      position: "relative",
      display: "inline-block",
      width: "100%",
      height: "100%",
      // 이 앱엔 전역 border-box 가 없다. content-box 로 두면 좌우 여백만큼 슬라이드보다 넓어져
      // 마지막 카드가 swiper(overflow:hidden) 오른쪽 끝에서 잘린다.
      boxSizing: "border-box",
      px: "6px",
      borderRadius: "10px",
      cursor: disabled ? "default" : "pointer",
      // 선은 inset 그림자로 그린다. border 를 켜고 끄면 그 두께만큼 내용이 밀린다.
      // 아무것도 고르지 않았을 때도 옅은 선은 남긴다 — 이게 없으면 버전들이 한 덩어리로 보인다.
      boxShadow: selected
        ? `inset 0 0 0 2px ${theme.palette.primary.main}`
        : `inset 0 0 0 1px ${theme.palette.grey[300]}`,
      transition: theme.transitions.create("box-shadow", { duration: theme.transitions.duration.shorter }),
      "&:hover": disabled || selected ? {} : { boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.55)}` },
      // 떠오르는 건 내용물뿐이다. 테두리까지 같이 올리면 swiper(overflow:hidden)가 위쪽을 잘라낸다.
      "&:hover .comparison-float": disabled ? {} : { transform: "translateY(-4px)" },
      ...sx,
    })}>
    <Box
      className="comparison-float"
      sx={theme => ({
        height: "100%",
        // transform 을 항상 걸어 둔다. hover 때만 생기면 그 순간 절대배치 자식(자물쇠 버튼 등)의
        // 기준 박스가 슬라이드에서 이 박스로 바뀌면서 위치가 튄다.
        transform: "translateY(0)",
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
      })}>
      {children}
    </Box>
  </Box>
);

const comparisonSelection = {
  ComparisonReleaseBar,
  SelectableComparison,
};

export default comparisonSelection;
